import CheckmarkIcon from '@assets/icons/checkmark-thin.svg';
import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { Linking, ScrollView, View } from 'react-native';
import { TabCardDetails } from '@/components/payments/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useTabCardImage } from '@/hooks/payments/use-tab-card-image';
import { useToasts } from '@/contexts/toast-context';

export interface TabCardApplicationContentProps {
  onAfterSignUp: () => void;
  tabCardDetails: TabCardDetails;
}

export const TabCardApplicationContent: React.FC<
  TabCardApplicationContentProps
> = ({ onAfterSignUp, tabCardDetails }) => {
  const { cashbackPercentage, distributorName, imageUrl, signUpUrl } =
    tabCardDetails;
  const { source, onImageLoadError } = useTabCardImage(imageUrl);
  const [styles, theme] = useStyles();
  const { showErrorToast } = useToasts();

  const handleSignUp = useCallback(async () => {
    try {
      await Linking.openURL(signUpUrl);
    } catch (e) {
      showErrorToast();
    }
    onAfterSignUp();
  }, [signUpUrl, onAfterSignUp, showErrorToast]);

  const cashBackCopy =
    cashbackPercentage > 0
      ? [
          i18n.t('tab_card_modal.cash_back_on_purchases', {
            cashbackPercentage: String(cashbackPercentage),
            distributorName,
          }),
          i18n.t('tab_card_modal.cash_back_everywhere'),
        ]
      : [
          i18n.t('tab_card_modal.cash_back_on_select_products', {
            distributorName,
          }),
        ];

  const benefitsCopy = [
    ...cashBackCopy,
    i18n.t('tab_card_modal.expense_management_portal'),
  ];

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.cardContainer}>
        <CachedImage
          accessibilityIgnoresInvertColors
          alt=""
          onError={onImageLoadError}
          source={source}
          style={styles.cardImage}
        />
      </View>
      <View style={styles.textContent}>
        <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
          {i18n.t('tab_card_modal.loyalty_program_intro', { distributorName })}
        </Typography>
        <View style={styles.benefitsList}>
          {benefitsCopy.map((text) => (
            <View key={text} style={styles.benefitItem}>
              <CheckmarkIcon
                height={16}
                stroke={theme.sk.color.icon.primary}
                width={16}
              />
              <View style={styles.benefitText}>
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-md"
                >
                  {text}
                </Typography>
              </View>
            </View>
          ))}
        </View>
        <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
          {i18n.t('tab_card_modal.apply_for_free')}
        </Typography>
      </View>
      <View style={styles.setupButtonContainer}>
        <Button
          onPress={handleSignUp}
          style={styles.setupButton}
          trackingData={{ name: 'tab_card_application.sign_up_button' }}
        >
          {i18n.t('tab_card_modal.set_up_your_account')}
        </Button>
      </View>
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  benefitItem: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  benefitsList: {
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  benefitText: {
    flex: 1,
  },
  cardContainer: {
    alignItems: 'center',
    marginBottom: theme.sk.spacing.spaceLarge,
    marginTop: theme.sk.spacing.spaceSmall,
  },
  cardImage: {
    height: 206,
    maxWidth: '100%',
    width: 327,
  },
  scrollContent: {
    flexGrow: 1,
  },
  setupButton: {
    borderRadius: theme.sk.radii.radiusMedium,
  },
  setupButtonContainer: {
    alignItems: 'flex-end',
  },
  textContent: {
    gap: theme.sk.spacing.spaceMedium,
    marginBottom: theme.sk.spacing.spaceMedium,
  },
}));
